import { useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { theme as defaultTheme } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { Typography } from "components/typography";
import { AiPreferences } from "components/psychologist/aiPreferences";
import { LocalSettings } from "./LocalSettings";
import { PasskeySettings } from "./passkeys";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../base";
import { styled } from "styled-components";

const StyledTabs = styled(Tabs)`
  margin-bottom: ${defaultTheme.MARGIN.XL};
`;

const StyledTab = styled(Tab)`
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_BLACK};
    transition: all 200ms ease-in-out;
  }
`;

type TabConfig = {
  key: string;
  label: string;
  content: React.ReactNode;
};

export const UserSettingsPage = () => {
  const { t } = useTranslation();
  const isLlmFeedbackEnabled = useFlag("ICBT_GENERATE_LLM_FEEDBACK");
  const isCustomPromptsEnabled = useFlag("ICBT_LLM_FEEDBACK_CUSTOM_PROMPTS");
  const isLocalSettingsEnabled = useFlag("MINDLERCARE_LOCAL_SETTINGS");
  const [activeTab, setActiveTab] = useState(0);

  const tabs: TabConfig[] = [
    ...(isLlmFeedbackEnabled && isCustomPromptsEnabled
      ? [
          {
            key: "ai-preferences",
            label: t("USER_SETTINGS.TAB_AI_PREFERENCES"),
            content: <AiPreferences />,
          },
        ]
      : []),
    {
      key: "passkeys",
      label: t("USER_SETTINGS.TAB_PASSKEYS"),
      content: <PasskeySettings />,
    },
    ...(isLocalSettingsEnabled
      ? [
          {
            key: "local-settings",
            label: t("USER_SETTINGS.TAB_LOCAL_SETTINGS"),
            content: <LocalSettings />,
          },
        ]
      : []),
  ];

  const onTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <PageContainer style={{ gap: defaultTheme.PADDING.XL }}>
      <Typography insensitive variant="h1">
        {t("USER_SETTINGS.PAGE_TITLE")}
      </Typography>
      {tabs.length > 1 && (
        <StyledTabs
          value={activeTab}
          onChange={onTabChange}
          style={{ marginBottom: 0 }}
        >
          {tabs.map((tab) => (
            <StyledTab key={tab.key} label={tab.label} disableRipple />
          ))}
        </StyledTabs>
      )}
      {tabs[activeTab]?.content}
    </PageContainer>
  );
};
